import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Box, useMediaQuery } from "@mui/material";
import { ResponsiveLine, LineSeries, LineCustomSvgLayer } from "@nivo/line";
import { useTheme } from "@mui/material/styles";
import ChartHeader, { ToggleOption } from "./ChartHeader";
import ChartEmptyState from "./ChartEmptyState";
import ChartCardWrapper from "./ChartCardWrapper";
import ChartModal from "./ChartModal";
import ChartTooltipPortal from "./ChartTooltipPortal";

const getTickLabels = (
  allLabels: string[],
  isMobile: boolean,
): string[] | undefined => {
  const count = allLabels.length;

  let step: number | undefined;
  if (isMobile) {
    if (count <= 8) step = undefined;
    else if (count <= 16) step = 2;
    else if (count <= 24) step = 3;
    else step = 4;
  } else {
    if (count <= 12) step = undefined;
    else if (count <= 24) step = 2;
    else step = 3;
  }

  if (!step) return undefined;
  return allLabels.filter((_, i) => i % step === 0);
};

export interface GenericFinancialLineChartProps {
  title: string;
  ticker?: string;
  height?: number;
  isModal?: boolean;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
  onCloseModal?: () => void;

  isLoading: boolean;
  error: Error | null;

  // Optional switch in the header (e.g. period), styled like the bar chart's.
  headerToggleOptions?: ToggleOption[];
  headerToggleValue?: string;
  onHeaderToggleChange?: (value: string) => void;
  headerTitleAlign?: "left" | "center";
  headerTitleColor?: string;

  chartData: LineSeries[];
  isEmpty: boolean;

  yAxisLegend?: string;
  yAxisFormat: (value: number) => string;
  xAxisTickRotation?: number;

  colors: (serie: LineSeries) => string;
  lineWidth?: number;
  lineLayer?: LineCustomSvgLayer<LineSeries>;
  pointSize?: number;
  curve?: string;
  enableArea?: boolean;
  areaOpacity?: number;
  // Where the area fill ends; defaults to nivo's scale minimum.
  areaBaselineValue?: number;
  // Vertical grid lines at the bottom-axis ticks, giving the cross grid the
  // bar charts have.
  enableGridX?: boolean;
  enableCrosshair?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defs?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fill?: any[];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tooltip?: (point: any) => React.ReactNode;

  // When true, use nivo's x-slice hover (continuous vertical hit areas) rather
  // than the default per-point voronoi mesh. Prevents tooltip flicker on dense
  // series (e.g. daily data) where mesh cells become tiny and the tooltip
  // unmounts/remounts rapidly between them.
  useSlices?: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customLayers?: any[];

  // Layers that should render *above* the data line (e.g. reference-value
  // labels that would otherwise be obscured by the line itself). Inserted
  // after "points" but before "slices"/"mesh" so they still don't block
  // hover interactivity.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  topLayers?: any[];

  belowChartContent?: React.ReactNode;
  growthMetrics?: React.ReactNode;
  // Suppress the Insight Analytics watermark in modal view. Useful when
  // belowChartContent renders interactive controls (e.g. toggles) that
  // would otherwise sit beneath the absolutely-positioned logo.
  hideLogoInModal?: boolean;

  leftMargin?: number;
  rightMargin?: number;
  bottomMargin?: number;
  axisTickFontSize?: number;
  axisBottomTickPadding?: number;
  axisLeftTickPadding?: number;

  axisBottomTickValues?: string[];
  // Formats bottom-axis labels, so points can be keyed on a unique value (a
  // date) while the axis shows a friendlier label.
  axisBottomFormat?: (value: string) => string;

  noDataTitle?: string;
  noDataDescription?: string;
}

const GenericFinancialLineChart: React.FC<GenericFinancialLineChartProps> = (
  props
) => {
  const {
    title,
    height = 400,
    isModal = false,
    renderCard = true,
    showFullscreen = false,
    noHover = false,
    onCloseModal,
    isLoading,
    error,
    headerToggleOptions,
    headerToggleValue,
    onHeaderToggleChange,
    headerTitleAlign,
    headerTitleColor,
    chartData,
    isEmpty,
    yAxisLegend,
    yAxisFormat,
    xAxisTickRotation = -45,
    colors,
    lineWidth = 2.5,
    lineLayer,
    pointSize = 6,
    curve = "monotoneX",
    enableArea = false,
    areaOpacity = 0.06,
    areaBaselineValue,
    enableGridX = false,
    enableCrosshair: enableCrosshairProp = true,
    defs: defsProp,
    fill: fillProp,
    tooltip,
    useSlices = false,
    customLayers,
    topLayers,
    belowChartContent,
    growthMetrics,
    hideLogoInModal = false,
    leftMargin,
    rightMargin,
    bottomMargin,
    axisTickFontSize,
    axisBottomTickPadding,
    axisLeftTickPadding,
    axisBottomTickValues,
    axisBottomFormat,
    noDataTitle,
    noDataDescription,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [modalOpen, setModalOpen] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mobileTooltip, setMobileTooltip] = useState<{ point: any; x: number; y: number } | null>(null);
  const mobileTouchOriginRef = useRef<{ y: number } | null>(null);

  useEffect(() => {
    if (!isMobile || !mobileTooltip) return;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) mobileTouchOriginRef.current = { y: t.clientY };
      if (!chartContainerRef.current?.contains(e.target as Node)) {
        setMobileTooltip(null);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || !mobileTouchOriginRef.current) return;
      if (Math.abs(t.clientY - mobileTouchOriginRef.current.y) > 10) {
        setMobileTooltip(null);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [isMobile, mobileTooltip]);

  if (isLoading || error || isEmpty) {
    return (
      <ChartCardWrapper renderCard={renderCard}>
        <ChartEmptyState
          isLoading={isLoading}
          error={error}
          isEmpty={!isLoading && !error && isEmpty}
          height={height}
          title={title}
          noDataTitle={noDataTitle}
          noDataDescription={noDataDescription}
        />
      </ChartCardWrapper>
    );
  }

  const effectiveLeftMargin = leftMargin ?? (isMobile ? 70 : 85);
  const effectiveRightMargin = rightMargin ?? (isMobile ? 10 : 20);
  const effectiveBottomMargin = bottomMargin ?? (isMobile ? 50 : 60);
  const effectiveTickFontSize = axisTickFontSize ?? (isMobile ? 10 : 11);

  const bottomTickValues = axisBottomTickValues ?? (() => {
    const firstSeries = chartData[0];
    if (!firstSeries?.data || firstSeries.data.length === 0) return undefined;

    const allLabels = firstSeries.data.map((d) => String(d.x));
    return getTickLabels(allLabels, isMobile);
  })();

  const chartContent = (
    <>
      <Box sx={isModal ? { px: 2, pt: 2 } : undefined}>
        <ChartHeader
          title={title}
          show={!noHover}
          toggleOptions={headerToggleOptions}
          toggleValue={headerToggleValue}
          onToggleChange={onHeaderToggleChange}
          titleAlign={headerTitleAlign}
          titleColor={headerTitleColor}
          showFullscreen={showFullscreen}
          isModal={isModal}
          onOpenModal={() => setModalOpen(true)}
          onCloseModal={onCloseModal}
        />
      </Box>

      <Box
        ref={chartContainerRef}
        onClick={isMobile ? (e: React.MouseEvent) => {
          if (mobileTooltip) {
            e.stopPropagation();
            setMobileTooltip(null);
          }
        } : undefined}
        sx={{
          height,
          ...(isModal && { flex: 1, minHeight: 0 }),
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(0, 0, 0, 0.015)",
          borderRadius: 1,
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <ResponsiveLine
          {...(lineLayer && { role: "group", ariaLabel: title })}
          data={chartData}
          margin={{
            top: isMobile ? 10 : 20,
            right: effectiveRightMargin,
            bottom: effectiveBottomMargin,
            left: effectiveLeftMargin,
          }}
          xScale={{ type: "point" }}
          yScale={{
            type: "linear",
            min: "auto",
            max: "auto",
            stacked: false,
          }}
          colors={colors}
          lineWidth={lineWidth}
          curve={curve as never}
          pointSize={pointSize}
          pointColor={{ theme: "background" }}
          pointBorderWidth={2}
          pointBorderColor={{ from: "serieColor" }}
          enablePointLabel={false}
          enableArea={enableArea}
          areaOpacity={areaOpacity}
          {...(areaBaselineValue !== undefined ? { areaBaselineValue } : {})}
          {...(defsProp ? { defs: defsProp } : {})}
          {...(fillProp ? { fill: fillProp } : {})}
          {...(customLayers || topLayers || lineLayer
            ? {
                layers: [
                  "grid",
                  "markers",
                  "axes",
                  ...(customLayers ?? []),
                  "crosshair",
                  lineLayer ?? "lines",
                  "points",
                  ...(topLayers ?? []),
                  "slices",
                  "mesh",
                  "legends",
                ],
              }
            : {})}
          enableGridX={enableGridX && !isMobile}
          {...(enableGridX && bottomTickValues ? { gridXValues: bottomTickValues } : {})}
          enableGridY={true}
          gridYValues={isMobile ? 4 : 5}
          useMesh={!noHover && !useSlices}
          enableSlices={!noHover && useSlices ? "x" : false}
          enableCrosshair={!noHover && enableCrosshairProp}
          crosshairType="bottom"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: axisBottomTickPadding ?? (isMobile ? 8 : 12),
            tickRotation: xAxisTickRotation,
            ...(bottomTickValues ? { tickValues: bottomTickValues } : {}),
            ...(axisBottomFormat ? { format: axisBottomFormat } : {}),
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: axisLeftTickPadding ?? (isMobile ? 8 : 5),
            tickRotation: 0,
            legend: isMobile ? undefined : yAxisLegend,
            legendPosition: "middle",
            legendOffset: -(effectiveLeftMargin - 15),
            format: yAxisFormat,
            tickValues: isMobile ? 4 : 5,
          }}
          theme={{
            background: "transparent",
            text: {
              fontSize: isMobile ? 10 : 12,
              fill: theme.palette.text.primary,
              outlineWidth: 0,
              outlineColor: "transparent",
            },
            axis: {
              domain: {
                line: { stroke: "transparent", strokeWidth: 0 },
              },
              legend: {
                text: {
                  fontSize: isMobile ? 10 : 12,
                  fill: theme.palette.text.primary,
                  fontWeight: 600,
                },
              },
              ticks: {
                line: { stroke: "transparent", strokeWidth: 0 },
                text: {
                  fontSize: effectiveTickFontSize,
                  fill: theme.palette.text.primary,
                  fontWeight: 600,
                },
              },
            },
            grid: {
              line: {
                stroke: theme.palette.divider,
                strokeWidth: 1,
                strokeOpacity: 0.2,
              },
            },
            crosshair: {
              line: {
                stroke: theme.palette.text.secondary,
                strokeWidth: 1,
                strokeOpacity: 0.5,
              },
            },
            tooltip: {
              container: tooltip
                ? {
                    background: "transparent",
                    border: "none",
                    borderRadius: 0,
                    boxShadow: "none",
                    padding: 0,
                  }
                : {
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    fontSize: 12,
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[8],
                    border: `1px solid ${theme.palette.divider}`,
                  },
            },
          }}
          isInteractive={!noHover}
          {...(!noHover && tooltip && useSlices
            ? {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                sliceTooltip: isMobile ? () => null : ({ slice }: { slice: any }) => {
                  const point = slice?.points?.[0];
                  if (!point) return null;
                  const rect = chartContainerRef.current?.getBoundingClientRect();
                  const anchorX = rect
                    ? rect.left + effectiveLeftMargin + point.x
                    : undefined;
                  return (
                    <ChartTooltipPortal anchorX={anchorX} containerRef={chartContainerRef}>
                      {tooltip(point)}
                    </ChartTooltipPortal>
                  );
                },
              }
            : {})}
          {...(!noHover && tooltip && !useSlices
            ? {
                // On mobile the hover tooltip is replaced by tap-toggle state.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                tooltip: isMobile ? () => null : ({ point }: { point: any }) => {
                  const rect = chartContainerRef.current?.getBoundingClientRect();
                  const anchorX = rect
                    ? rect.left + effectiveLeftMargin + point.x
                    : undefined;
                  return (
                    <ChartTooltipPortal anchorX={anchorX} containerRef={chartContainerRef}>
                      {tooltip(point)}
                    </ChartTooltipPortal>
                  );
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onClick: isMobile ? (point: any, event: React.MouseEvent) => {
                  event.stopPropagation();
                  setMobileTooltip(prev =>
                    prev?.point.id === point.id
                      ? null
                      : { point, x: event.clientX, y: event.clientY }
                  );
                } : undefined,
              }
            : {})}
        />
      </Box>

      {isModal && growthMetrics}

      {isModal && !growthMetrics && !hideLogoInModal && (
        <Box sx={{ position: "relative", pb: 1.5, px: 2 }}>
          {belowChartContent}
          <Box
            component="img"
            src={
              theme.palette.mode === "dark"
                ? "/insightLongLogoPrimaryWhiteText.svg"
                : "/insightLongLogoPrimary.svg"
            }
            alt="Insight Analytics"
            sx={{
              height: 32,
              display: "block",
              position: "absolute",
              right: 16,
              bottom: 12,
            }}
          />
        </Box>
      )}

      {isModal && !growthMetrics && hideLogoInModal && (
        <Box sx={{ pb: 1.5, px: 2 }}>{belowChartContent}</Box>
      )}

      {(!isModal || growthMetrics) && belowChartContent}

      {isMobile && mobileTooltip && tooltip && ReactDOM.createPortal(
        <div
          style={{
            position: "fixed",
            left: Math.max(148, Math.min(mobileTooltip.x, window.innerWidth - 148)),
            top: mobileTooltip.y - 24,
            transform: "translate(-50%, -100%)",
            zIndex: 99999,
            pointerEvents: "none",
          }}
        >
          {tooltip(mobileTooltip.point)}
        </div>,
        document.body
      )}
    </>
  );

  return (
    <>
      <ChartCardWrapper renderCard={renderCard} isModal={isModal}>
        {chartContent}
      </ChartCardWrapper>

      {!isModal && (
        <ChartModal open={modalOpen} onClose={() => setModalOpen(false)}>
          <GenericFinancialLineChart
            {...props}
            isModal={true}
            renderCard={false}
            height={700}
            showFullscreen={false}
            onCloseModal={() => setModalOpen(false)}
          />
        </ChartModal>
      )}
    </>
  );
};

export default GenericFinancialLineChart;
